import { Injectable, OnDestroy } from '@angular/core';
import { ReminderDto } from '@authority-labs/api-interfaces';
import { LIMIT } from '@authority-labs/constants';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface NotificationBroadcastData {
  list: ReminderDto[];
  activeCount: number;
  missedCount: number;
}

interface BroadcastMessage {
  type: 'notifications-updated' | 'request-sync' | 'reminder-mutated';
  data?: NotificationBroadcastData;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationBroadcastService implements OnDestroy {
  private channel: BroadcastChannel;
  private messages$ = new Subject<BroadcastMessage>();

  readonly onNotificationsReceived$: Observable<NotificationBroadcastData> =
    this.messages$.pipe(
      filter((msg) => msg.type === 'notifications-updated'),
      map((msg) => msg.data)
    );

  readonly onSyncRequested$: Observable<void> = this.messages$.pipe(
    filter((msg) => msg.type === 'request-sync'),
    map(() => undefined)
  );

  readonly onMutation$: Observable<void> = this.messages$.pipe(
    filter((msg) => msg.type === 'reminder-mutated'),
    map(() => undefined)
  );

  constructor() {
    this.channel = new BroadcastChannel(LIMIT.DATA_CHANNEL_NAME);
    this.channel.onmessage = (event) => {
      this.messages$.next(event.data);
    };
  }

  broadcastNotifications(
    list: ReminderDto[],
    activeCount: number,
    missedCount: number
  ): void {
    this.post({
      type: 'notifications-updated',
      data: { list, activeCount, missedCount }
    });
  }

  requestSync(): void {
    this.post({ type: 'request-sync' });
  }

  broadcastMutation(): void {
    this.post({ type: 'reminder-mutated' });
  }

  ngOnDestroy(): void {
    try {
      this.channel.close();
    } catch {
      // Already closed
    }
    this.messages$.complete();
  }

  private post(message: BroadcastMessage): void {
    try {
      this.channel.postMessage(message);
    } catch {
      // Channel may be closed
    }
  }
}
